import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Handshake, Plus } from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';
import { useCreateMeeting, useMeetings } from '../../../hooks/useMeetingLog';
import { usePermissions } from '../../../hooks/usePermissions';
import { meetingTypeLabel, type Meeting, type MeetingInput } from '../../../lib/meetingLogApi';
import { MeetingFormModal } from '../../meetings/MeetingFormModal';
import { IngestStatusBadge } from '../../meetingNotes/IngestStatusBadge';

/**
 * The lead's meetings (Întâlniri module): every logged meeting whose `leadId`
 * is this lead, newest first. Each row opens the meeting page, where the
 * transcript, the minutes and the AI task suggestions live. "Add meeting"
 * opens the shared meeting form with the lead preselected. Reads go through
 * the meetings list API, so the RBAC scope of the `meetings` module applies
 * (an 'involved' viewer sees only the meetings they were part of).
 */
export function LeadMeetingsSection({ leadId }: { leadId: string }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canView = can('meetings', 'canView');
  const canCreate = can('meetings', 'canCreate');
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, isError } = useMeetings(
    { leadId, limit: 50, sort: '-startAt' },
    { enabled: canView },
  );
  const createMutation = useCreateMeeting();
  const meetings = data?.data ?? [];

  if (!canView) return null;

  async function handleCreate(input: MeetingInput) {
    await createMutation.mutateAsync({ ...input, leadId: input.leadId ?? leadId });
    setShowCreate(false);
  }

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-700">
          <Handshake size={15} className="text-gray-500" />
          {t('leads.meetings.title')}
          {meetings.length > 0 && (
            <span className="font-normal text-gray-400">({meetings.length})</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[12px]">
          {meetings.length > 0 && (
            <Link
              to={`/meetings?leadId=${encodeURIComponent(leadId)}`}
              className="inline-flex items-center gap-1 text-gray-500 hover:text-brand-700"
            >
              {t('leads.meetings.viewAll')} <ExternalLink size={12} />
            </Link>
          )}
          {canCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus size={13} /> {t('leads.meetings.add')}
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white text-gray-900">
        {isLoading ? (
          <p className="p-4 text-sm text-gray-400">…</p>
        ) : isError ? (
          <p className="p-4 text-sm text-red-600">{t('leads.meetings.error')}</p>
        ) : meetings.length === 0 ? (
          <div className="p-4">
            <p className="text-sm text-gray-500">{t('leads.meetings.empty')}</p>
            <p className="mt-1 text-xs text-gray-400">{t('leads.meetings.hint')}</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {meetings.map((m) => (
              <MeetingRow key={m.id} meeting={m} />
            ))}
          </ul>
        )}
      </div>

      {showCreate && (
        <MeetingFormModal
          defaults={{ leadId }}
          submitting={createMutation.isPending}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}
    </section>
  );
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('ro-RO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MeetingRow({ meeting: m }: { meeting: Meeting }) {
  const { t } = useI18n();
  const attendees = m.attendees
    .map((a) => (a.companyName ? `${a.fullName} (${a.companyName})` : a.fullName))
    .join(', ');
  return (
    <li>
      <Link to={`/meetings/${m.id}`} className="block px-4 py-3 hover:bg-gray-50">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 text-sm font-medium text-gray-900">{m.title}</p>
          <span className="shrink-0 whitespace-nowrap text-xs text-gray-500">
            {formatWhen(m.startAt)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-500">
          {meetingTypeLabel(m.meetingType)}
          {' · '}
          {m.owner.name}
          {m.teamMembers.length > 0 && ` +${m.teamMembers.length}`}
          {m.project && ` · ${m.project.code ?? m.project.name}`}
        </p>
        {attendees && <p className="mt-1 line-clamp-1 text-xs text-gray-700">{attendees}</p>}
        <div className="mt-1.5 text-xs text-gray-600">
          {m.latestNote ? (
            <span className="inline-flex items-center gap-1.5">
              <IngestStatusBadge status={m.latestNote.ingestStatus as never} />
              {m.notesCount > 1 && <span>×{m.notesCount}</span>}
            </span>
          ) : (
            <span className="text-gray-400">{t('meetings.noNotes')}</span>
          )}
        </div>
      </Link>
    </li>
  );
}
